import api from './api'

const getToken = () => {
  const localToken = localStorage.getItem('access_token')
  if (localToken) {
    return localToken
  }

  const match = document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/)
  return match ? match[1] : null
}

const setToken = (token) => {
  if (!token) return

  localStorage.setItem('access_token', token)

  const isProduction = import.meta.env.PROD

  if (isProduction) {
    document.cookie = `access_token=${token}; path=/; secure; samesite=strict; max-age=43200`
  } else {
    document.cookie = `access_token=${token}; path=/; max-age=43200`
  }
}

export const authService = {
  async login(email, password) {
    try {
      const loginData = {
        email: email,
        password: password
      }

      const response = await api.post('/auth/login', loginData)

      if (response.data?.access_token) {
        setToken(response.data.access_token)
        return response.data
      }
      throw new Error('Invalid response format - no access token received')
    } catch (error) {
      console.error('❌ Login error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })

      if (error.response?.status === 422) {
        const errorDetail = error.response?.data?.detail
        if (errorDetail) {
          throw new Error(`Validation error: ${JSON.stringify(errorDetail)}`, { cause: error })
        } else {
          throw new Error('Invalid login data format. Please check your credentials.', {
            cause: error
          })
        }
      }
      if (error.response?.status === 401) {
        throw new Error('Invalid credentials', { cause: error })
      }
      if (error.response?.status === 404) {
        throw new Error('Login endpoint not found. Please check your API configuration.', {
          cause: error
        })
      }
      throw error
    }
  },

  async signup(userData) {
    try {
      const signupData = {
        firstname: userData.firstname,
        lastname: userData.lastname,
        email: userData.email,
        password: userData.password,
        role_id: userData.role_id,
        // Le back attend establishment_ids (liste). On accepte aussi un
        // establishment_id (singulier) par robustesse.
        establishment_ids:
          userData.establishment_ids ??
          (userData.establishment_id ? [userData.establishment_id] : []),
        is_active: userData.is_active !== undefined ? userData.is_active : true
      }

      const response = await api.post('/auth/signup', signupData)

      return response.data
    } catch (error) {
      console.error('❌ Signup error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })

      if (error.response?.status === 422) {
        const errorDetail = error.response?.data?.detail
        if (errorDetail) {
          throw new Error(`Validation error: ${JSON.stringify(errorDetail)}`, { cause: error })
        } else {
          throw new Error('Invalid signup data format.', { cause: error })
        }
      }
      if (error.response?.status === 400) {
        throw new Error('Email already exists or invalid data', { cause: error })
      }
      if (error.response?.status === 409) {
        throw new Error('User with this email already exists', { cause: error })
      }
      if (error.response?.status === 404) {
        throw new Error('Signup endpoint not found. Please check your API configuration.', {
          cause: error
        })
      }
      throw error
    }
  },

  async createUser(userData) {
    return authService.signup(userData)
  },

  async updateUser(userId, userData) {
    try {
      const updateData = {
        firstname: userData.firstname,
        lastname: userData.lastname,
        email: userData.email,
        role_id: userData.role_id,
        establishment_id: userData.establishment_id
      }

      if (userData.password) {
        updateData.password = userData.password
      }

      const response = await api.patch(`/user/${userId}`, updateData)
      return response.data
    } catch (error) {
      console.error('❌ Update user error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers
      })

      if (error.response?.status === 422) {
        const errorDetail = error.response?.data?.detail
        if (errorDetail) {
          throw new Error(`Validation error: ${JSON.stringify(errorDetail)}`, { cause: error })
        } else {
          throw new Error('Invalid update data format.', { cause: error })
        }
      }
      if (error.response?.status === 404) {
        throw new Error('User not found', { cause: error })
      }
      throw error
    }
  },

  async testLogin(email, password) {
    const testFormats = [
      { email, password },

      new URLSearchParams({ email, password }),

      { username: email, password },
      { user: email, pass: password },

      { credentials: { email, password } },

      { email, password, grant_type: 'password' }
    ]

    for (let i = 0; i < testFormats.length; i++) {
      try {
        const config = {
          headers:
            testFormats[i] instanceof URLSearchParams
              ? { 'Content-Type': 'application/x-www-form-urlencoded' }
              : { 'Content-Type': 'application/json' }
        }

        const response = await api.post('/auth/login', testFormats[i], config)
        return response.data
      } catch (error) {
        // Continue to next format
      }
    }

    throw new Error('All login formats failed')
  },

  async getCurrentUser() {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No token found')
      }

      const response = await api.get('/auth/get_current_user')

      return response
    } catch (error) {
      console.error('❌ Get current user failed:', error)
      throw error
    }
  },

  async logout() {
    try {
      localStorage.removeItem('access_token')
      document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout error:', error)
    }
  },

  async verifyToken() {
    try {
      const token = getToken()
      if (!token) {
        throw new Error('No token found')
      }

      const response = await api.get('/auth/verify')
      return response.data
    } catch (error) {
      console.error('Token verification failed:', error)
      throw error
    }
  },

  getToken,
  setToken
}

export default authService
